import { useState, useRef, useCallback, useEffect } from 'react';
import {
  Box,
  Text,
  VStack,
  HStack,
  Button,
  Image,
  IconButton,
  Alert,
} from '@chakra-ui/react';
import { MdAdd, MdClose, MdCloudUpload } from 'react-icons/md';
import { RecipeClient } from '@/lib/recipeClient';
import { uploadToMediaStorage, generateImportMediaKey } from '@/lib/mediaUtils';
import { toaster } from '@/lib/toaster';
import { useImportSheet } from '@/contexts/ImportSheetContext';

interface SelectedImage {
  file: File;
  preview: string;
  id: string;
}

export default function ImportImagePanel() {
  const { startPolling, isPolling, closeExistingToast } = useImportSheet();
  const [selectedImages, setSelectedImages] = useState<SelectedImage[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = useCallback((files: FileList) => {
    const validFiles: SelectedImage[] = [];
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'];
    const maxSize = 10 * 1024 * 1024; // 10 MB

    Array.from(files).forEach((file) => {
      if (!validTypes.includes(file.type)) {
        toaster.create({
          title: 'Invalid file type',
          description: `${file.name} is not a supported image format. Please use JPEG, PNG, WebP, or GIF.`,
          type: 'error',
        });
        return;
      }
      if (file.size > maxSize) {
        toaster.create({
          title: 'File too large',
          description: `${file.name} is larger than 10MB. Please choose a smaller image.`,
          type: 'error',
        });
        return;
      }
      validFiles.push({
        file,
        preview: URL.createObjectURL(file),
        id: `${file.name}-${Date.now()}-${Math.random()}`,
      });
    });

    if (validFiles.length > 0) {
      setSelectedImages((prev) => [...prev, ...validFiles]);
      setUploadError('');
    }
  }, []);

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(false);
      if (e.dataTransfer.files.length > 0) handleFileSelect(e.dataTransfer.files);
    },
    [handleFileSelect],
  );

  const handleRemoveImage = (id: string) => {
    setSelectedImages((prev) => {
      const img = prev.find((i) => i.id === id);
      if (img) URL.revokeObjectURL(img.preview);
      return prev.filter((i) => i.id !== id);
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (selectedImages.length === 0) {
      setUploadError('Please select at least one image');
      return;
    }

    setUploadError('');
    setIsUploading(true);

    try {
      closeExistingToast();

      const uploadToastId = toaster.create({
        title: 'Uploading images…',
        description: `Uploading ${selectedImages.length} image${selectedImages.length > 1 ? 's' : ''} to server.`,
        type: 'loading',
      });

      const mediaKeys: string[] = [];
      for (const { file } of selectedImages) {
        const ext = file.name.split('.').pop() ?? 'jpg';
        const mediaKey = generateImportMediaKey(ext, file.name);
        await uploadToMediaStorage(file, mediaKey);
        mediaKeys.push(mediaKey);
      }

      toaster.dismiss(uploadToastId);

      const processingToastId = toaster.create({
        title: 'Submitting images…',
        description: 'Sending your images for processing',
        type: 'loading',
      });

      const response = await RecipeClient.importFromImages({ mediaKeys });
      toaster.dismiss(processingToastId);

      startPolling(response.jobId);
      setIsUploading(false);
    } catch (error) {
      closeExistingToast();
      setIsUploading(false);
      const msg = error instanceof Error ? error.message : 'Unknown error occurred';
      setUploadError(`Failed to upload images: ${msg}`);
      toaster.create({ title: 'Error processing images', description: msg, type: 'error' });
    }
  };

  // Clean up object URLs on unmount
  useEffect(() => {
    return () => {
      selectedImages.forEach((img) => URL.revokeObjectURL(img.preview));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isProcessing = isUploading || isPolling;

  return (
    <form onSubmit={handleSubmit}>
      <VStack gap={4} align="stretch">
        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/jpg,image/png,image/webp,image/gif"
          multiple
          onChange={(e) => { if (e.target.files) handleFileSelect(e.target.files); }}
          style={{ display: 'none' }}
        />

        {selectedImages.length === 0 ? (
          /* Drop zone */
          <Box
            border="2px dashed"
            borderColor={isDragOver ? 'accent.secondary' : 'accent.secondaryBorder'}
            borderRadius="12px"
            py={8}
            px={4}
            textAlign="center"
            bg="accent.secondarySubtle"
            _hover={{ borderColor: 'accent.secondary' }}
            cursor="pointer"
            onClick={() => fileInputRef.current?.click()}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            transition="all 0.2s"
          >
            <VStack gap={3}>
              <MdCloudUpload size={40} color="var(--chakra-colors-accent-secondary)" />
              <VStack gap={1}>
                <Text fontFamily="heading" fontWeight="800" fontSize="md" color="text.primary">
                  Drop photos here
                </Text>
                <Text fontSize="sm" fontWeight="300" color="text.muted">
                  or tap to select from your camera roll
                </Text>
              </VStack>
              <Text fontSize="xs" color="text.muted">
                JPEG, PNG, WebP, GIF — up to 10MB each
              </Text>
            </VStack>
          </Box>
        ) : (
          /* Image previews */
          <VStack gap={3} w="100%">
            <HStack justify="space-between" w="100%">
              <Text fontFamily="body" fontWeight="600" fontSize="sm" color="text.primary">
                {selectedImages.length} image{selectedImages.length > 1 ? 's' : ''} selected
              </Text>
              <Button
                variant="outline"
                size="xs"
                borderColor="accent.secondary"
                color="accent.secondary"
                _hover={{ bg: 'accent.secondarySubtle' }}
                onClick={() => fileInputRef.current?.click()}
                disabled={isProcessing}
              >
                <MdAdd />
                Add more
              </Button>
            </HStack>

            <VStack gap={2} w="100%">
              {selectedImages.map((image) => (
                <HStack
                  key={image.id}
                  w="100%"
                  bg="bg.surface"
                  border="1px solid"
                  borderColor="border.muted"
                  borderRadius="8px"
                  overflow="hidden"
                  gap={0}
                >
                  <Image
                    src={image.preview}
                    alt={image.file.name}
                    objectFit="cover"
                    w="56px"
                    h="56px"
                    flexShrink={0}
                  />
                  <Text fontSize="xs" color="text.muted" flex={1} px={3} lineClamp={2}>
                    {image.file.name}
                  </Text>
                  <IconButton
                    aria-label="Remove image"
                    size="sm"
                    variant="ghost"
                    color="text.muted"
                    _hover={{ color: 'text.primary' }}
                    onClick={() => handleRemoveImage(image.id)}
                    disabled={isProcessing}
                    mr={1}
                  >
                    <MdClose />
                  </IconButton>
                </HStack>
              ))}
            </VStack>
          </VStack>
        )}

        {uploadError && (
          <Alert.Root status="error" borderRadius="8px">
            <Alert.Indicator />
            <Alert.Description fontSize="sm">{uploadError}</Alert.Description>
          </Alert.Root>
        )}

        <Button
          type="submit"
          bg="accent.emphasis"
          color="white"
          _hover={{ bg: 'accent.emphasisHover' }}
          size="md"
          w="100%"
          loading={isProcessing}
          loadingText={isUploading ? 'Uploading…' : 'Processing…'}
          disabled={selectedImages.length === 0}
        >
          Extract recipe
        </Button>
      </VStack>
    </form>
  );
}
